import type { UseCase } from '@/components/onboarding/types';
import type { Language } from '@/i18n/translations';
import { getToolPreset } from '@/lib/toolPresets';

export const USECASE_GLOSSARY: Record<UseCase, Partial<Record<Language, string>> & { en: string }> = {
  'it-infrastructure': {
    sv: 'Använd IT-topology-vokabulär: routers, switches, firewalls, VLANs, AWS/Azure/GCP, on-prem/hybrid, nodes, dependencies, bottlenecks. Exempel-prompts: "Rita bird\'s-eye topology med bottlenecks markerade", "Gruppera services per säkerhetszon".',
    en: 'Use IT topology vocabulary: routers, switches, firewalls, VLANs, AWS/Azure/GCP, on-prem/hybrid, nodes, dependencies, bottlenecks. Example prompts: "Draw a bird\'s-eye topology with bottlenecks marked", "Group services by security zone".',
  },
  'business-process': {
    sv: 'Använd BPMN/process-flow-vokabulär: swimlanes, gateways, linear/branching/complex flows, decision points, approvals. Exempel: "HR-onboarding med 4 condition blocks", "DevOps CI/CD med approval-gates".',
    en: 'Use BPMN/process-flow vocabulary: swimlanes, gateways, linear/branching/complex flows, decision points, approvals. Examples: "HR onboarding with 4 condition blocks", "DevOps CI/CD with approval gates".',
  },
  'organization-chart': {
    sv: 'Använd org-chart-vokabulär: hierarchy, RACI, CEO→VP→teams, stakeholders, departments, reporting lines. Exempel: "Projekt-RACI med 25 roller", "Team composition med skills-badges".',
    en: 'Use org-chart vocabulary: hierarchy, RACI, CEO→VP→teams, stakeholders, departments, reporting lines. Examples: "Project RACI with 25 roles", "Team composition with skills badges".',
  },
  'decision-tree': {
    sv: 'Använd troubleshooting/rules-vokabulär: condition blocks, diagnosis paths, Yes/No-branches, shallow/medium/deep trees. Exempel: "Troubleshooting-guide med 5 levels", "Kund-eligibility quiz".',
    en: 'Use troubleshooting/rules vocabulary: condition blocks, diagnosis paths, Yes/No branches, shallow/medium/deep trees. Examples: "Troubleshooting guide with 5 levels", "Customer eligibility quiz".',
  },
  'mind-map': {
    sv: 'Använd mind-map-vokabulär: radial hub, hierarchical, free-form, branches, themes. Korta block-namn (3-5 ord). Exempel: "Fri idé-generering kring nytt projekt", "Brainstorm med 4 huvudgrenar".',
    en: 'Use mind-map vocabulary: radial hub, hierarchical, free-form, branches, themes. Short block names (3-5 words). Examples: "Free idea generation around a new project", "Brainstorm with 4 main branches".',
  },
  'project-planning': {
    sv: 'Använd projekt-vokabulär: milestones, sprints, Kanban, roadmap, dependencies, time horizons. Exempel: "3-månaders sprint-flow med blockers", "Produktroadmap Now/Next/Later".',
    en: 'Use project vocabulary: milestones, sprints, Kanban, roadmap, dependencies, time horizons. Examples: "3-month sprint flow with blockers", "Product roadmap Now/Next/Later".',
  },
  sandbox: {
    sv: 'Du är en kreativ, experimentell partner. Inga restriktioner — var lekfull, föreslå oväntade kombinationer och bryt mot konventioner när det känns rätt.',
    en: 'You are a creative, experimental partner. No restrictions — be playful, suggest unexpected combinations and break conventions when it feels right.',
  },
};

export function buildUseCaseLayer(useCase: UseCase, lang: Language): string {
  const entry = USECASE_GLOSSARY[useCase] ?? USECASE_GLOSSARY.sandbox;
  const glossary = entry[lang] ?? entry.en;
  const preset = getToolPreset(useCase);
  const toolVocab = preset.map(t => ({
    type: t.type,
    label: t.label[lang] ?? t.label.en,
  }));
  const toolLine = lang === 'sv'
    ? `Block-etiketter att använda (underliggande type är stabil): ${JSON.stringify(toolVocab)}`
    : `Block labels to use (underlying type stays stable): ${JSON.stringify(toolVocab)}`;
  return `${glossary}\n\n${toolLine}`;
}
